/**
 * The global filter bar's state, and the logic that applies it.
 *
 * Three of these filters are CLAIM-GRAIN: claim type, service type and claim status
 * describe a claim, while every row in this bundle describes a person. A bundle built
 * from 2026-10-07 splits each person by claim (data/claimCells.ts), and the filters add
 * up the cells they select — exact. An older bundle has no such split; there they
 * reweight each person by their observed share — see `modeledWeight` for exactly how
 * far that is and is not an estimate.
 *
 * Shore used to be one of them. It is not: the curated model carries an on/off flag on
 * every employee, and every composition row is 100% one shore, because shore is a
 * property of the PERSON and not of the claim. Reweighting by it was worse than
 * redundant — a person whose only shore key was "On" got `byShore['Off'] ?? 1`, so
 * filtering to off-shore left them in scope at FULL weight and the filter did nothing.
 * Vendor and office location, added 2026-09-22, are person attributes for the same
 * reason and are filtered exactly.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { liveState } from '@/ui/urlState'
import { PF_KEY, type PersonFilters } from '@/state/sharedView'
import { activeInRange, asRanked, buildCohorts, type Person, type Derived } from './selectors'
import type { CompositionRow, CrosstabDict, TouchMonthRow } from './types'
import { claimFilterNote, exactPerson, selectedCells, type ClaimSplit } from './claimCells'
import { inTree } from '@/measures/teams'
import { groupRows, laborWeight, rowShares, sliceWeights, type SliceMatch, type Weight } from '@/measures/trend'
import {
  loadStoredFilters,
  saveStoredFilters,
  clearStoredFilters,
  parseStoredFilters,
  pruneFilters,
  type FilterOptions,
} from './filterStorage'

export const ALL = '__all__'

export type EmploymentStatus = 'active' | 'both' | 'terminated'
export type ManagementFilter = 'all' | 'yes' | 'no'

/**
 * Tenure bands, verbatim from Shayna. The stored value is the array index; each band
 * is a half-open range in years, so a boundary lands in exactly one band.
 */
export const TENURE_BANDS: { label: string; min: number; max: number }[] = [
  { label: 'Under 1 yr', min: 0, max: 1 },
  { label: '1–2 yrs', min: 1, max: 2 },
  { label: '2–4 yrs', min: 2, max: 4 },
  { label: '4–7 yrs', min: 4, max: 7 },
  { label: '7–12 yrs', min: 7, max: 12 },
  { label: '12+ yrs', min: 12, max: Infinity },
]

/**
 * Benefit-loaded payroll across the whole period, not annualised — which is why a band
 * that looks low for a salary is not: it covers eight months, and anyone who joined
 * part-way through sits lower still.
 */
export const PAYROLL_BANDS: { label: string; min: number; max: number }[] = [
  { label: 'Under $10K', min: 0, max: 10_000 },
  { label: '$10K–20K', min: 10_000, max: 20_000 },
  { label: '$20K–30K', min: 20_000, max: 30_000 },
  { label: '$30K–45K', min: 30_000, max: 45_000 },
  { label: '$45K–75K', min: 45_000, max: 75_000 },
  { label: '$75K and up', min: 75_000, max: Infinity },
]

/**
 * The multi-select filters. An EMPTY array means "all", not "none" — a filter nobody
 * has touched must not empty the page, and clearing the last checkbox is the same
 * gesture as pressing reset on that one control.
 */
export const MULTI_KEYS = [
  'claimType',
  'serviceType',
  'claimStatus',
  'facilityGroup',
  'department',
  'jobTitle',
  'shore',
  'vendor',
  'officeLocation',
] as const

export type MultiKey = (typeof MULTI_KEYS)[number]

export interface Filters {
  claimType: string[]
  serviceType: string[]
  claimStatus: string[]
  /**
   * Facility groups, by name ("Unknown" stands for every blank spelling). Where a row
   * carries the facility — the Overview's crosstab bars, Facilities, Claim and Touch
   * detail — it selects rows exactly. Everywhere else each person is seen through their
   * share of claims and labor in the groups, off the crosstab: modelled, badged M (see
   * `GROUP_NOTE`).
   */
  facilityGroup: string[]
  department: string[]
  jobTitle: string[]
  shore: string[]
  vendor: string[]
  officeLocation: string[]
  /** Single-select: the Employee and Supervisor pages each show exactly one. */
  supervisor: string
  employee: string
  management: ManagementFilter
  employmentStatus: EmploymentStatus
  /** Index into TENURE_BANDS, or ALL. */
  tenure: string
  /** Index into PAYROLL_BANDS, or ALL. */
  payrollBand: string
  monthFrom: string
  monthTo: string
  /**
   * A named set of people, set by picking a bar on the Overview ("Open in
   * Employees" for a state, payer, facility group or facility). Exact, like the
   * other person filters; cleared from its scope pill; never remembered.
   *
   * `match` is the bar itself. With it, each person's figures are their share of the
   * slice — claims by their claim share, dollars by their labor share, off the
   * crosstab, as the Overview's trend takes a picked bar — rather than their whole
   * book (review #177: the "Unknown" facility group's 555 people opened in Employees
   * at their full figures, so nothing on the page was about Unknown). Modelled, like a
   * claim filter. Claim and Touch detail filter their rows on it exactly.
   */
  slice: { label: string; employees: string[]; match?: SliceMatch } | null
}

export const EMPTY_FILTERS: Filters = {
  claimType: [],
  serviceType: [],
  claimStatus: [],
  facilityGroup: [],
  department: [],
  jobTitle: [],
  shore: [],
  vendor: [],
  officeLocation: [],
  supervisor: ALL,
  employee: ALL,
  management: 'all',
  // Was 'active' until 2026-09-25. Picking someone who has left — by name, on a detail
  // page — then showed nothing with no reason given (`PC110230`: 276 claims,
  // terminated 2026-08-07). Everyone by default until the filters are rethought.
  employmentStatus: 'both',
  tenure: ALL,
  payrollBand: ALL,
  monthFrom: ALL,
  monthTo: ALL,
  slice: null,
}

/**
 * The claim-grain filters: applied by selecting each person's claim cells where the
 * bundle carries them (data/claimCells.ts), and by reweighting where it does not.
 *
 * Claim type and service type joined this list on 2026-09-22 and claim status was
 * already on it. What changed at the same time is how good the reweight is — see
 * `modeledWeight`. The name is older than the cells; the list is the claim filters.
 */
export const MODELED_KEYS = ['claimType', 'serviceType', 'claimStatus'] as const

export const hasModeled = (f: Filters) => MODELED_KEYS.some((k) => f[k].length > 0)

/** Labels for the controls and the scope pills. One place, so they cannot drift. */
export const FILTER_LABELS: Record<MultiKey, string> = {
  claimType: 'Claim type',
  serviceType: 'Service type',
  claimStatus: 'Claim status',
  facilityGroup: 'Facility group',
  department: 'Department',
  jobTitle: 'Job title',
  shore: 'Is on shore',
  vendor: 'Vendor',
  officeLocation: 'Office location',
}

/** A multi-select matches when it is empty (all) or contains the value. */
const matches = (sel: string[], value: string | null) =>
  sel.length === 0 || (value !== null && sel.includes(value))

interface FilterCtx {
  filters: Filters
  set: <K extends keyof Filters>(key: K, value: Filters[K]) => void
  /** Add or remove one value from a multi-select. */
  toggle: (key: MultiKey, value: string) => void
  /** Clear one control back to "all". */
  clear: (key: keyof Filters) => void
  /**
   * Everything back to a bare page: the bar, and the page's own picks and personal
   * state. `keep` names query params that say WHERE the page is (Facilities'
   * `facility`), which survive.
   */
  reset: (keep?: readonly string[]) => void
  /**
   * Replace every filter at once — a review comment reopening the view it was written
   * on. Missing keys fall back to their defaults, so a comment captured by an older
   * build still restores.
   */
  restore: (f: Partial<Filters>) => void
  /**
   * Drop any remembered value the loaded bundle no longer offers. Called once the
   * option lists exist — the provider restores before it can know them.
   */
  prune: (options: FilterOptions) => void
  /** Controls away from their default, as applied on this page (see `carriedEmployee`). */
  activeCount: number
  /**
   * On a page where Employee is page-level (`EMPLOYEE_PAGE_SCOPED`): the Employee
   * filter set on another page, which this page shows and does not apply. Null
   * everywhere else, and once it is picked or applied here.
   */
  carriedEmployee: string | null
  /** Apply the carried Employee filter on this page, as if it were picked here. */
  applyCarriedEmployee: () => void
}

/**
 * Pages where the Employee filter is PAGE-LEVEL. Everywhere else it follows you, as
 * every other filter does. Here it applies only when it was picked on this page (on
 * this history entry): one picked on My metrics or Employees used to cut the
 * Supervisors cards to the teams that person is on, and the Scorecard to their one
 * row, with only a pill to say so (Asana 1218881614527003). It is not cleared — the
 * bar says it is set on another page and offers to apply it here or clear it — and the
 * pages you go on to still have it.
 */
export const EMPLOYEE_PAGE_SCOPED: readonly string[] = ['/supervisors', '/scorecard']

export const employeePageScoped = (pathname: string) => EMPLOYEE_PAGE_SCOPED.includes(pathname)

/** How many controls are away from their default. */
const countActive = (f: Filters) =>
  (Object.keys(EMPTY_FILTERS) as (keyof Filters)[]).filter((k) => {
    const v = f[k]
    return Array.isArray(v) ? v.length > 0 : v !== EMPTY_FILTERS[k]
  }).length

const Ctx = createContext<FilterCtx | null>(null)

export function useFilters(): FilterCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useFilters must be used inside <FilterProvider>')
  return ctx
}

/* ------------------------------------------------------------------ in the URL */

/**
 * Where each name-free filter sits in the query string. Prefixed, so no page's own
 * parameter (`dept`, `pick`, `group`…) can collide with one. Multi-selects are joined on
 * "|", as ui/urlState.ts's `list` is. Supervisor, Employee and the slice name people, so
 * they are never here: they sit in the history entry (`PF_KEY`), and a shared link
 * carries them as opaque keys (state/sharedView.ts).
 */
export const FILTER_PARAMS = {
  claimType: 'f.ct',
  serviceType: 'f.sv',
  claimStatus: 'f.cs',
  facilityGroup: 'f.fg',
  department: 'f.dep',
  jobTitle: 'f.jt',
  shore: 'f.sh',
  vendor: 'f.vn',
  officeLocation: 'f.ol',
  management: 'f.mg',
  employmentStatus: 'f.es',
  tenure: 'f.ten',
  payrollBand: 'f.pay',
  monthFrom: 'f.from',
  monthTo: 'f.to',
} as const satisfies Record<Exclude<keyof Filters, 'supervisor' | 'employee' | 'slice'>, string>

type NameFreeKey = keyof typeof FILTER_PARAMS
const NAME_FREE = Object.keys(FILTER_PARAMS) as NameFreeKey[]

/** The filters into `params`: each control at its default is left out. */
export function writeFilterParams(params: URLSearchParams, f: Filters): void {
  for (const k of NAME_FREE) {
    const p = FILTER_PARAMS[k]
    params.delete(p)
    const v = f[k]
    if (Array.isArray(v)) {
      if (v.length) params.set(p, v.join('|'))
    } else if (v !== EMPTY_FILTERS[k]) params.set(p, v)
  }
}

export const hasFilterParams = (params: URLSearchParams) =>
  NAME_FREE.some((k) => params.has(FILTER_PARAMS[k]))

/**
 * The name-free filters a query string asks for, checked as a remembered value is
 * (filterStorage.ts): an unknown enum or band reads as its default. Whether a value
 * still exists in the bundle is `prune`'s job, as for a remembered one.
 */
export function readFilterParams(params: URLSearchParams): Pick<Filters, NameFreeKey> {
  const raw: Record<string, unknown> = {}
  for (const k of NAME_FREE) {
    const v = params.get(FILTER_PARAMS[k])
    if (v === null) continue
    raw[k] = (MULTI_KEYS as readonly string[]).includes(k) ? v.split('|').filter(Boolean) : v
  }
  const f = parseStoredFilters(JSON.stringify({ v: 2, filters: raw }))
  const out = {} as Record<NameFreeKey, unknown>
  for (const k of NAME_FREE) out[k] = f[k]
  return out as Pick<Filters, NameFreeKey>
}

/** The person parts of the filters, as the history entry keeps them: defaults left out. */
export function personParts(f: Filters): PersonFilters {
  const out: PersonFilters = {}
  if (f.supervisor !== ALL) out.supervisor = f.supervisor
  if (f.employee !== ALL) out.employee = f.employee
  if (f.slice) out.slice = f.slice
  return out
}

type Loc = { search: string; state: unknown }

const pfOf = (state: unknown): PersonFilters | undefined => {
  const pf = (state as Record<string, unknown> | null)?.[PF_KEY]
  return pf && typeof pf === 'object' ? (pf as PersonFilters) : undefined
}

/**
 * Whether this history entry says what its filters are. It does once the app has been
 * on it (the provider stamps every entry it sees), or when its URL names any filter or
 * is a shared link (`v`). An entry that does not — a tab's bare link, a drill-through
 * pushed by a page — keeps whatever the bar already had.
 */
export function isStamped(at: Loc): boolean {
  if (pfOf(at.state)) return true
  const params = new URLSearchParams(at.search)
  return params.has('v') || hasFilterParams(params)
}

/** The filters a stamped entry holds. */
export function readFilters(at: Loc): Filters {
  const pf = pfOf(at.state) ?? {}
  const slice = pf.slice
  return {
    ...EMPTY_FILTERS,
    ...readFilterParams(new URLSearchParams(at.search)),
    supervisor: typeof pf.supervisor === 'string' ? pf.supervisor : ALL,
    employee: typeof pf.employee === 'string' ? pf.employee : ALL,
    slice:
      slice && typeof slice.label === 'string' && Array.isArray(slice.employees)
        ? slice
        : null,
  }
}

/**
 * The filter bar, kept in the history entry: name-free controls in the query string,
 * the people (Supervisor, Employee, a slice) in router state. One source of truth —
 * Back and Forward put the bar back as it was on that entry, a reload keeps it, and a
 * copied link opens it (state/sharedView.ts).
 *
 * localStorage still remembers the bar between visits (filterStorage.ts), but only
 * fills a first entry that says nothing of its own: opening the app at a bare address.
 * A shared link always says (`v=`), so it opens exactly as sent, clean if it was clean.
 *
 * Writes replace the current entry, like ui/urlState.ts's, and read the live URL and
 * state, so several changes in one click (the Overview does this) all land.
 */
export function FilterProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const stamped = isStamped(location)

  // The bar as last written or read. Fills an entry that does not say (see isStamped);
  // on the very first one, that is the remembered bar.
  const latest = useRef<Filters | null>(null)
  if (latest.current === null) latest.current = stamped ? readFilters(location) : loadStoredFilters()

  // The same object until something actually changes: a page-setting write is a new
  // location too, and a fresh object would rerun every rollup below.
  const cache = useRef<{ key: string; f: Filters } | null>(null)
  const read = stamped ? readFilters(location) : latest.current
  const readKey = JSON.stringify(read)
  if (cache.current?.key !== readKey) cache.current = { key: readKey, f: read }
  const raw = cache.current.f
  latest.current = raw

  // Employee, where it is page-level and was not picked on this entry: shown, not applied.
  const here = stamped && pfOf(location.state)?.employeeHere === true
  const carriedEmployee =
    employeePageScoped(location.pathname) && !here && raw.employee !== ALL ? raw.employee : null
  const filters = useMemo(
    () => (carriedEmployee === null ? raw : { ...raw, employee: ALL }),
    [raw, carriedEmployee],
  )

  /*
   * `here`: whether Employee counts as picked on this entry. Left out, a write keeps
   * what the entry had; a new entry starts without it. It is kept only where Employee
   * is page-level and set, so every other entry's state is as it was.
   */
  const write = useCallback(
    (next: Filters, opts: { clearPage?: boolean; keep?: readonly string[]; here?: boolean } = {}) => {
      const params = new URLSearchParams(opts.clearPage ? '' : window.location.search)
      // A cleared page keeps only what says where it is (a facility opened), never a
      // filter or a pick.
      if (opts.clearPage) {
        const was = new URLSearchParams(window.location.search)
        for (const k of opts.keep ?? []) for (const v of was.getAll(k)) params.append(k, v)
      }
      writeFilterParams(params, next)
      const state = opts.clearPage ? {} : liveState()
      const pf = personParts(next)
      const wasHere = (state[PF_KEY] as PersonFilters | undefined)?.employeeHere === true
      if ((opts.here ?? wasHere) && next.employee !== ALL && employeePageScoped(window.location.pathname))
        pf.employeeHere = true
      const qs = params.toString()
      const search = qs ? `?${qs}` : ''
      latest.current = next
      if (
        !opts.clearPage &&
        search === window.location.search &&
        JSON.stringify(state[PF_KEY]) === JSON.stringify(pf)
      )
        return
      state[PF_KEY] = pf
      navigate({ pathname: window.location.pathname, search, hash: window.location.hash }, { replace: true, state })
    },
    [navigate],
  )

  // An entry that does not say what its filters are gets the bar's, so Back to it later
  // finds them. Keyed on the entry: a page's own write is a new location too.
  useEffect(() => {
    if (!stamped) write(latest.current!, { here: false })
  }, [location.key, stamped, write])

  // Remembered for the next visit; a bar with nothing set clears the key rather than
  // storing a copy of the defaults, so Reset and "clear the last pill" leave the same
  // nothing behind.
  useEffect(() => {
    if (countActive(raw) === 0) clearStoredFilters()
    else saveStoredFilters(raw)
  }, [raw])

  const update = useCallback(
    (fn: (f: Filters) => Filters, here?: boolean) => write(fn(latest.current!), { here }),
    [write],
  )

  const prune = useCallback(
    (options: FilterOptions) => {
      const f = latest.current!
      const next = pruneFilters(f, options)
      if (next !== f) write(next)
    },
    [write],
  )

  const value = useMemo<FilterCtx>(() => {
    const activeCount = countActive(filters)

    return {
      filters,
      // Picking an Employee picks it here too, where it is page-level.
      set: (key, v) => update((f) => ({ ...f, [key]: v }), key === 'employee' ? v !== ALL : undefined),
      toggle: (key, v) =>
        update((f) => {
          const cur = f[key]
          return {
            ...f,
            [key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v],
          }
        }),
      clear: (key) => update((f) => ({ ...f, [key]: EMPTY_FILTERS[key] })),
      // Everything back to a bare page: the bar, and the page's own picks with it.
      reset: (keep) => {
        clearStoredFilters()
        write(EMPTY_FILTERS, { clearPage: true, keep })
      },
      // A captured view holds the filters as they applied, so an Employee in it applied.
      restore: (f) => write({ ...EMPTY_FILTERS, ...f }, { here: !!f.employee && f.employee !== ALL }),
      prune,
      activeCount,
      carriedEmployee,
      applyCarriedEmployee: () => write(latest.current!, { here: true }),
    }
  }, [filters, prune, update, write, carriedEmployee])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/* ------------------------------------------------------------------ applying */

export interface Scope {
  /** People surviving the exact filters, with modeled weights already applied. */
  people: Person[]
  /**
   * `people` before the Supervisor filter. Supervisor teams are built from this, so
   * picking one supervisor never changes who they rank against (MEASURE-RULES §10.4)
   * — it only chooses whose team is on screen.
   */
  teamPool: Person[]
  /**
   * `teamPool` as it is RANKED, whatever the Employee status control says: the same
   * filters, people who had left before the range removed, and anyone who left during
   * it entered over the months they were active (`asRanked`; review #61, 2026-10-05).
   * Supervisor teams are ranked on teams built from this (see `teamRanking` in
   * measures/teams.ts), so the status control never moves a team's rank.
   */
  rankPool: Person[]
  /**
   * `teamPool` with the Employee filter left off: every filter but who is picked. My
   * metrics narrows to one person through that filter, and still shows the people
   * who roll up to them when that person supervises others.
   */
  reportPool: Person[]
  /** `reportPool` as `rankPool` is to `teamPool`: as ranked. My metrics' team view. */
  reportRankPool: Person[]
  /**
   * `teamPool` with Job title and Employee left off. The Supervisors page builds WHOLE
   * teams from this and uses those two filters to choose which cards show — a
   * supervisor by their own title, a person by the team they are on. Building teams
   * from `teamPool` instead cut every team down to the people with that title or
   * name, so picking "Medicaid Supervisor" showed their managers' cards and none of
   * theirs (Shayna's review #21, 2026-10-02).
   *
   * The Management toggle does not narrow it either (review #106, 2026-10-06). Teams
   * are built from everyone, and the toggle goes in as `teamCounts`: it decides whose
   * labor and claims the figures add up. It no longer decides who has a card or who
   * is on a team. Narrowing the pool lost the card of a supervisor whose claims were
   * their own, and every manager's direct reports, because those are supervisors.
   */
  supervisorPool: Person[]
  /** `supervisorPool` as `rankPool` is to `teamPool`: as ranked. */
  supervisorRankPool: Person[]
  /**
   * The Management toggle as `buildTeams` / `teamRanking` take it: whether a person's
   * labor and claims count in a team built from `supervisorPool`. Pass it wherever
   * those two pools go.
   */
  teamCounts: (p: Person) => boolean
  /**
   * EVERY person, reweighted by the claim filters but not narrowed by anything. Look a
   * person up here, not in `derived.byName`, so a drill-through opened from a row
   * shows the same figures as the row.
   */
  byName: Map<string, Person>
  /**
   * Job-title cohorts. Taken from the reweighted population, so under a claim filter a
   * person's filtered figure ranks against their peers' filtered figures, not their
   * unfiltered ones. No person filter narrows them: department, title, supervisor,
   * employee, tenure, pay band, vendor, office, shore, slice, Employee status and —
   * since review #62, 2026-10-05 — the Management toggle never change who a person is
   * measured against. The toggle used to, as in the Ledger; but no job title mixes
   * management and staff, so all it did was empty a supervisor's own title and leave
   * them "the only one" with it.
   *
   * People active in the range only, whatever the Employee status control shows; anyone
   * who left during it is in for the months they were active (Shayna, 2026-09-25 and
   * review #61, 2026-10-05). The control decides who is listed, never who is a peer.
   */
  cohorts: Map<string, Person[]>
  /**
   * True when a claim filter leaves any figure on screen an allocation rather than a
   * count: always on a bundle without the claim cells, and only for weighted claims on
   * one with them. `modeledNote` names what.
   */
  modeled: boolean
  /** What the claim filters still model, in words; null when nothing. See `claimFilterNote`. */
  modeledNote: string | null
  /** A claim filter is on and the bundle's cells answer it exactly. */
  exact: boolean
  /**
   * Each person's figures under the claim filters as a share of their unfiltered ones,
   * claims and labor apart — 1 and 1 with no claim filter. For the grains the cells do
   * not split, such as the Overview's crosstab bars, which scale each person's rows by
   * it so they still add up to the tiles.
   */
  claimShare: (name: string) => { claims: number; labor: number }
  /** Months the month-based visuals should show. */
  months: string[]
  total: number
  carrying: number
}

/**
 * The last month that has closed, when the bundle ends in months that have not — the
 * default end of the range. Null when every month is closed, so nothing changes.
 *
 * A running month's ratios are noise, not an early read: pay and billing land on
 * different schedules, so on 2026-09-24 September showed MLTSS at $8,672 a claim (one
 * claim billed) and Medicaid at $7. Shading the month did not stop anyone reading
 * those, so it is left out unless somebody picks it.
 */
export function lastClosedMonth(months: readonly string[], openMonths: readonly string[]): string | null {
  const open = new Set(openMonths)
  let i = months.length - 1
  if (i < 0 || !open.has(months[i]!)) return null
  while (i >= 0 && open.has(months[i]!)) i--
  return i >= 0 ? months[i]! : null
}

/**
 * The month range the bar asks for, as `derive` takes it: ALL becomes an open end, and
 * a start picked after the end is read as the same span the other way round rather
 * than as an empty one — nobody choosing "Jun – Mar" means "no months".
 *
 * With no end picked, the range stops at `closedThrough` (see `lastClosedMonth`) —
 * unless the start picked is itself past it, which is somebody asking for the open
 * month on purpose.
 */
export function monthRangeOf(
  f: Pick<Filters, 'monthFrom' | 'monthTo'>,
  closedThrough: string | null = null,
): {
  from: string | null
  to: string | null
} {
  const from = f.monthFrom === ALL ? null : f.monthFrom
  const to =
    f.monthTo !== ALL
      ? f.monthTo
      : closedThrough !== null && (from === null || from <= closedThrough)
        ? closedThrough
        : null
  if (from !== null && to !== null && from > to) return { from: to, to: from }
  return { from, to }
}

/** Sum of a person's shares for the selected values. Empty selection = 1. */
const shareOf = (map: Record<string, number> | undefined, sel: string[]): number => {
  if (sel.length === 0) return 1
  if (!map) return 0
  let s = 0
  for (const v of sel) s += map[v] ?? 0
  return s
}

/**
 * Reweights one person by their observed share of the claim-grain filters.
 *
 * Returns SEPARATE weights for counts and for dollars, because they are different
 * numbers. A biller whose claims are 90% Inhouse can easily carry most of their labor
 * on the Ancillary 10% — Ancillary work runs at several times the Inhouse rate. The
 * earlier single weight scaled dollars by the claim share, which reported that
 * person's blended rate under a per-type label.
 *
 * Both weights come from `FactClaimAllocations` joined to `DimClaimType`, so for
 * claims and claim labor this is not an estimate at all: each person's split is exact
 * and the slice sums to the true slice. What stays modelled is every OTHER per-person
 * measure — resolved claims, touches, facilities, the open pile — none of which the
 * bundle splits by claim type. Those are scaled by the claim weight, and that is what
 * the MODELLED flag is warning about.
 *
 * Returns 1 when the filter is off or the person has NO composition row — a person the
 * composition never saw must not be silently zeroed out.
 *
 * A person who HAS a row but no entry for the selected value is a different case: the
 * composition did see them, and it says none of their claims are in that bucket. That
 * is a share of 0, not an unknown. Defaulting it to 1 — which is what this did — meant
 * a person with only resolved claims stayed at full weight under a filter for open
 * ones, so the modelled filters quietly did nothing for anybody who was all one thing.
 */
function modeledWeight(
  comp: Map<string, CompositionRow>,
  p: Person,
  f: Filters,
): { claims: number; labor: number } {
  const row = comp.get(p.employee)
  if (!row) return { claims: 1, labor: 1 }

  const claims =
    shareOf(row.byStatus, f.claimStatus) *
    shareOf(row.byClaimType, f.claimType) *
    shareOf(row.byServiceType, f.serviceType)

  // `laborByX` arrives with the 2026-09-22 bundle. An older bundle in a browser cache
  // has only the claim shares, and falling back to those is the previous behaviour —
  // wrong in a known direction rather than a crash on a stale cache entry.
  const labor =
    shareOf(row.laborByStatus ?? row.byStatus, f.claimStatus) *
    shareOf(row.laborByClaimType ?? row.byClaimType, f.claimType) *
    shareOf(row.laborByServiceType ?? row.byServiceType, f.serviceType)

  return { claims, labor }
}

/** Whether the Employee status control, on its own, leaves this person out. */
export const hiddenByStatus = (f: Pick<Filters, 'employmentStatus'>, p: { terminated: boolean }) =>
  (f.employmentStatus === 'active' && p.terminated) ||
  (f.employmentStatus === 'terminated' && !p.terminated)

/** The Management toggle. Narrows who is listed and the teams; never the cohorts. */
const managementMatches = (f: Filters, p: Person) =>
  f.management === 'yes' ? p.management : f.management === 'no' ? !p.management : true

/**
 * One month row seen through a claim filter's weights, scaled as `reweighted` scales
 * the person: labor by the labor weight, every count by the claim weight. Rates that
 * are a count over a count are unchanged; facilities is a distinct count and cannot
 * be scaled; pay and hours belong to the person, not to a claim type.
 *
 * The person's period weight is applied to every month alike — the composition is not
 * split by month — so a month cell is modelled, but the months pooled are exactly the
 * filtered headline. Before this (review #156, 2026-10-06), My metrics' Month by month
 * grid read the UNFILTERED rows beside a filtered headline: $5.74 a claim above, ~$19
 * in every month below.
 */
function reweightedMonth(m: TouchMonthRow, w: Weight): TouchMonthRow {
  const c = (v: number) => v * w.claims
  const cn = (v: number | null | undefined) => (v == null ? v : v * w.claims)
  // A month that billed nothing takes the no-claim labor weight, where there is one.
  const lw = laborWeight(w, m)
  return {
    ...m,
    resolvedClaims: c(m.resolvedClaims),
    noTouch: c(m.noTouch),
    oneTouch: c(m.oneTouch),
    twoPlusTouch: c(m.twoPlusTouch),
    claimsTouched: c(m.claimsTouched),
    costPerResolvedClaim:
      w.claims === 0 ? m.costPerResolvedClaim : (m.costPerResolvedClaim * lw) / w.claims,
    billedClaims: c(m.billedClaims),
    weightedClaims: cn(m.weightedClaims) ?? undefined,
    claimLabor: m.claimLabor * lw,
    openClaims: c(m.openClaims),
    openClaimsWorked: c(m.openClaimsWorked),
    charge: cn(m.charge),
    chargeResolved: cn(m.chargeResolved),
    resolvedNoTouch: c(m.resolvedNoTouch),
    resolvedOneTouch: c(m.resolvedOneTouch),
    resolvedTwoPlusTouch: c(m.resolvedTwoPlusTouch),
    resolvedTouches: c(m.resolvedTouches),
  }
}

/**
 * One person seen through a weight: a claim filter's, the Facility group filter's or a
 * slice's. See `applyFilters`. With a separate no-claim weight (a crosstab share,
 * `rowShares`) the labor of the months that billed nothing takes it and the rest takes
 * `labor`, so the person's claim labor is still their months added up.
 */
function reweighted(p: Person, w: Weight): Person {
  const nc = w.noClaim ?? w.labor
  const months = p.months.map((m) => reweightedMonth(m, w))
  const monthLabor = p.months.reduce((a, m) => a + m.claimLabor, 0)
  const claimLabor =
    nc === w.labor || monthLabor === 0
      ? p.claimLabor * w.labor
      : (p.claimLabor * months.reduce((a, m) => a + m.claimLabor, 0)) / monthLabor
  return {
    ...p,
    months,
    // The month-grain charge series the trend reads collection off. Claim weight, as
    // `collection` below takes it.
    collectionMonths:
      p.collectionMonths &&
      Object.fromEntries(
        Object.entries(p.collectionMonths).map(([m, [total, resolved]]) => [
          m,
          [total * w.claims, resolved * w.claims] as [number, number],
        ]),
      ),
    claims: p.claims * w.claims,
    standardClaims: p.standardClaims * w.claims,
    weightedClaims: p.weightedClaims === null ? null : p.weightedClaims * w.claims,
    claimLabor,
    // Part of `claimLabor`: the labor of the months that billed nothing, so their weight.
    unattributedLabor: p.unattributedLabor * nc,
    resolvedClaims: p.resolvedClaims * w.claims,
    // The touch buckets and the open pile are not split by claim type either. Their
    // rates are unchanged; their say in a pooled rate — the masthead's 0% touch share,
    // a team's open-worked % — shrinks with the claims, as resolved claims' does, so
    // the tiles and the Overview's trend (measures/trend.ts) pool the same weights.
    noTouch: p.noTouch * w.claims,
    touchBase: p.touchBase * w.claims,
    openPileSum: p.openPileSum * w.claims,
    openWorkedSum: p.openWorkedSum * w.claims,
    // Cost per claim is now a ratio of two DIFFERENTLY weighted sums, so unlike
    // the single-weight version it genuinely moves when the filter changes.
    costPerClaim: p.claims === 0 || w.claims === 0 ? null : claimLabor / (p.claims * w.claims),
    billedPerMonth: (p.billedPerMonth ?? 0) * w.claims,
    // The charge sums are not split by claim type either, so both halves take
    // the claim weight: the person's own rate is unchanged and their say in a
    // pooled rate shrinks with their claims — modelled, like resolved claims.
    collection:
      p.collection === null
        ? null
        : {
            resolved: p.collection.resolved * w.claims,
            total: p.collection.total * w.claims,
          },
  }
}

/** What a slice opened from the Overview does to the figures (review #177). */
export const SLICE_NOTE =
  'Each person’s claims and claim labor are their share of the slice, exactly. Everything else is scaled by that share — allocations, not counts.'

/**
 * What the Facility group filter does where the rows carry no facility: the tiles, the
 * people pages, the teams, the scorecard and the trend. The Overview's bars, Facilities,
 * Claim detail, Touch detail and Worklist assignments select their rows by facility instead, exactly.
 */
export const GROUP_NOTE =
  'Facility group: each person’s claims and claim labor are their share in the picked groups, off the facility breakdown. Everything else — resolved claims, touches, the open pile, each month — is scaled by that share: allocations, not counts. Labor on no claim has no facility, so it drops out. Exact on the Overview’s bars, Facilities, Claim detail, Touch detail and Worklist assignments.'

/**
 * Each person's share of their claims and claim labor in the picked facility groups,
 * off the crosstab (`rowShares`): claims over their claims, labor on the groups' claims
 * over their labor on claims. Labor on no claim is filed under no facility, so its
 * weight is 0: under the filter a person's unattributed labor is gone. The same shares
 * the Overview's bars take a month range by, so the two tie over any range. Null when
 * the filter is off; a person with nothing in the groups is absent.
 */
export function groupWeights(ct: CrosstabDict, groups: readonly string[]): Map<string, Weight> | null {
  const within = groupRows(ct, groups)
  return within ? rowShares(ct, within) : null
}

/** A weight that leaves the person as they are. */
const isFull = (w: Weight) => w.claims === 1 && w.labor === 1 && (w.noClaim ?? w.labor) === 1

/** One person through a weight, and the person they are ranked as through the same. */
const viewedThrough = (p: Person, w: Weight): Person => {
  if (isFull(w)) return p
  const scaled = reweighted(p, w)
  return p.rankedAs ? { ...scaled, rankedAs: reweighted(p.rankedAs, w) } : scaled
}

export function applyFilters(
  derived: Derived,
  composition: CompositionRow[],
  f: Filters,
  /** The bundle's claim cells (`claimSplitOf`); null on a bundle without them. */
  split: ClaimSplit | null = null,
  /** The crosstab a slice's `match` is weighed on. Without it a slice is people only. */
  crosstab?: CrosstabDict,
): Scope {
  const comp = new Map(composition.map((c) => [c.employee, c]))
  const filtering = hasModeled(f)
  const cellsExact = filtering && split !== null
  const sel = cellsExact ? selectedCells(split.dict, f) : null
  const range = { months: derived.months, allMonths: derived.allMonths }
  const groupW = crosstab ? groupWeights(crosstab, f.facilityGroup) : null
  // A slice inside the groups is a share of the person's rows in them (`sliceWeights`).
  const sliceW =
    f.slice?.match && crosstab
      ? sliceWeights(crosstab, f.slice.match, groupW ? groupRows(crosstab, f.facilityGroup) : null)
      : null

  // Everybody is filtered, not only the people in scope: cohorts are drawn from
  // outside the scope, and they have to be seen through the same claim filter.
  const claimViewed = !filtering
    ? derived.people
    : sel
      ? derived.people.map((p) => exactPerson(p, split!.byName.get(p.employee), split!.dict, sel, range))
      : derived.people.map((p) => {
          const w = modeledWeight(comp, p, f)
          if (w.claims === 1 && w.labor === 1) return p
          const scaled = reweighted(p, w)
          // The person as ranked (someone who has left, over their active months) takes
          // the same weight, so their rank moves with the filter as their row does.
          return p.rankedAs ? { ...scaled, rankedAs: reweighted(p.rankedAs, w) } : scaled
        })
  const unfiltered = derived.byName
  const claimFiltered = new Map(claimViewed.map((p) => [p.employee, p]))
  const claimShare = (name: string) => {
    const was = unfiltered.get(name)
    const now = claimFiltered.get(name)
    if (!filtering || !was || !now) return { claims: 1, labor: 1 }
    return {
      claims: was.claims === 0 ? 0 : now.claims / was.claims,
      labor: was.claimLabor === 0 ? 0 : now.claimLabor / was.claimLabor,
    }
  }
  /*
   * The Facility group filter, then an Overview slice that carries its bar
   * (`Filters.slice`): each person seen through their share of each, on top of the
   * claim filters. `claimShare` above stays the claim filters' alone — the Overview's
   * bars select the groups' rows and apply the slice themselves.
   */
  const grouped = groupW
    ? claimViewed.map((p) => viewedThrough(p, groupW.get(p.employee) ?? { claims: 0, labor: 0, noClaim: 0 }))
    : claimViewed
  const groupSplits =
    groupW !== null &&
    [...groupW.values()].some((w) => (w.claims > 0 && w.claims < 1) || (w.labor > 0 && w.labor < 1))
  const viewed = sliceW ? grouped.map((p) => viewedThrough(p, sliceW(p))) : grouped
  const filteredBy = sliceW || groupW ? new Map(viewed.map((p) => [p.employee, p])) : claimFiltered
  const modeledNote = sliceW
    ? SLICE_NOTE
    : groupSplits
      ? GROUP_NOTE
      : claimFilterNote({
        filtering,
        exact: cellsExact,
        weighted: derived.people.some((p) => p.weightedClaims !== null),
      })

  const slice = f.slice ? new Set(f.slice.employees) : null
  // Every exact person filter except Employee status, which is applied separately:
  // the teams on screen follow it, the teams they are ranked as do not.
  const exact = (p: Person, anyEmployee = false, anyTitle = false, anyManagement = false) => {
    if (slice && !slice.has(p.employee)) return false
    // Nobody with nothing in the picked facility groups, as the Overview's bars leave
    // them out. Exact: the crosstab says whether a person has any row there.
    if (groupW && !groupW.has(p.employee)) return false
    if (!matches(f.department, p.department)) return false
    if (!anyTitle && !matches(f.jobTitle, p.jobTitle)) return false
    if (!matches(f.vendor, p.vendor)) return false
    if (!matches(f.officeLocation, p.officeLocation)) return false
    if (!anyEmployee && f.employee !== ALL && p.employee !== f.employee) return false
    // Exact, not modelled — shore is an attribute of the person. Somebody whose shore
    // is unobserved is excluded rather than assigned one.
    if (!matches(f.shore, p.shore)) return false
    if (!anyManagement && !managementMatches(f, p)) return false
    if (f.tenure !== ALL) {
      const band = TENURE_BANDS[Number(f.tenure)]
      // Unknown tenure is excluded when a band is chosen. Putting nulls in a band
      // would quietly assert something the data does not say.
      if (!band || p.tenureYears === null) return false
      if (p.tenureYears < band.min || p.tenureYears >= band.max) return false
    }
    if (f.payrollBand !== ALL) {
      const band = PAYROLL_BANDS[Number(f.payrollBand)]
      if (!band) return false
      if (p.payroll < band.min || p.payroll >= band.max) return false
    }
    return true
  }
  const teamPool = viewed.filter((p) => exact(p) && !hiddenByStatus(f, p))
  // Ranked as: people active in the range, each over the months they were active.
  const rankPool = viewed.filter((p) => exact(p) && activeInRange(p)).map(asRanked)
  const reportPool =
    f.employee === ALL ? teamPool : viewed.filter((p) => exact(p, true) && !hiddenByStatus(f, p))
  const reportRankPool =
    f.employee === ALL
      ? rankPool
      : viewed.filter((p) => exact(p, true) && activeInRange(p)).map(asRanked)
  const cardFilters = f.employee !== ALL || f.jobTitle.length > 0
  const supervisorPool = viewed.filter(
    (p) => exact(p, cardFilters, cardFilters, true) && !hiddenByStatus(f, p),
  )
  const supervisorRankPool = viewed
    .filter((p) => exact(p, cardFilters, cardFilters, true) && activeInRange(p))
    .map(asRanked)
  // A supervisor's whole tree, at every level, and the supervisor themself — the
  // same people measures/teams.ts rolls up, so the filtered page and the team card
  // quote the same cost per claim.
  const people =
    f.supervisor === ALL ? teamPool : teamPool.filter((p) => inTree(p, f.supervisor))

  // `derived` is already built over the selected range; see AppData and `derive`.
  const months = derived.months

  return {
    people,
    teamPool,
    rankPool,
    reportPool,
    reportRankPool,
    supervisorPool,
    supervisorRankPool,
    teamCounts: (p) => managementMatches(f, p),
    byName: filteredBy,
    // Whatever the status control and the Management toggle say: see `Scope.cohorts`.
    cohorts: buildCohorts(viewed),
    modeled: modeledNote !== null,
    modeledNote,
    exact: cellsExact,
    claimShare,
    months,
    total: people.length,
    carrying: people.filter((p) => p.claims > 0).length,
  }
}
